import { FC, useState, useMemo } from "react"
import { Compass, Loader2, Copy } from "lucide-react"
import { PrimaryBtn } from "../../buttons"
import diagnosticsApi from "../../../../api/diagnostics"
import { useAppDispatch } from "../../../hooks"
import { pushToast } from "../../../../redux/slices/notifications"

const US_STATES = [
  "AL",
  "AK",
  "AZ",
  "AR",
  "CA",
  "CO",
  "CT",
  "DE",
  "FL",
  "GA",
  "HI",
  "ID",
  "IL",
  "IN",
  "IA",
  "KS",
  "KY",
  "LA",
  "ME",
  "MD",
  "MA",
  "MI",
  "MN",
  "MS",
  "MO",
  "MT",
  "NE",
  "NV",
  "NH",
  "NJ",
  "NM",
  "NY",
  "NC",
  "ND",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VT",
  "VA",
  "WA",
  "WV",
  "WI",
  "WY"
]

// --- Output parser ---

interface KVEntry {
  key: string
  value: string
}

interface GenericTable {
  headers: string[]
  rows: string[][]
}

interface ParsedSection {
  title: string
  kvEntries: KVEntry[]
  tables: GenericTable[]
  textLines: string[]
}

/** Detect table header: all-caps line with many words or 3+ groups separated by 2+ spaces */
function isTableHeaderLine(trimmed: string): boolean {
  if (!trimmed || trimmed.startsWith("===")) return false
  if (!/^[A-Z][A-Z\s_]+$/.test(trimmed)) return false
  // Method 1: 3+ groups when split by wide gaps
  const wide = trimmed.split(/\s{2,}/)
  if (wide.length >= 3 && wide.every(p => /[A-Z]/.test(p))) return true
  // Method 2: 5+ uppercase words (handles narrow column gaps)
  const words = trimmed.split(/\s+/)
  return words.length >= 5
}

/** Check if line is a section header (all-caps, not indented, not a table header) */
function isSectionHeaderLine(line: string, trimmed: string): boolean {
  if (line.startsWith(" ")) return false
  if (isTableHeaderLine(trimmed)) return false
  return /^[A-Z][A-Z\s]+(\(.*\))?\s*(:.*)?$/.test(trimmed)
}

/**
 * Detect column boundaries by finding character positions where
 * ALL lines have a space. Columns start where content resumes after a gap.
 */
function detectColumnStarts(rawLines: string[]): number[] {
  const maxLen = Math.max(...rawLines.map(l => l.length))
  if (maxLen === 0) return [0]

  const isGap: boolean[] = new Array(maxLen).fill(true)
  for (const line of rawLines) {
    for (let j = 0; j < line.length; j++) {
      if (line[j] !== " ") isGap[j] = false
    }
  }

  // Column starts = transitions from gap → content
  const starts: number[] = []
  for (let j = 0; j < maxLen; j++) {
    if (!isGap[j] && (j === 0 || isGap[j - 1])) {
      starts.push(j)
    }
  }
  return starts
}

function sliceCells(line: string, colStarts: number[]): string[] {
  const cells: string[] = []
  for (let c = 0; c < colStarts.length; c++) {
    const start = colStarts[c]
    const end = c < colStarts.length - 1 ? colStarts[c + 1] : line.length
    cells.push(line.substring(start, end).trim())
  }
  return cells
}

function parseOutput(raw: string): ParsedSection[] {
  const lines = raw.split("\n")
  const sections: ParsedSection[] = []
  let current: ParsedSection | null = null
  let i = 0

  const ensureCurrent = () => {
    if (!current) {
      current = { title: "", kvEntries: [], tables: [], textLines: [] }
      sections.push(current)
    }
  }

  while (i < lines.length) {
    const line = lines[i]
    const trimmed = line.trim()

    // Skip empty and wrapper lines
    if (!trimmed || trimmed.match(/^===.*===\s*$/)) {
      i++
      continue
    }

    // Table header detection (checked before section header)
    if (isTableHeaderLine(trimmed)) {
      const headerLine = lines[i]
      i++

      // Skip separator line (dashes)
      if (i < lines.length && lines[i].trim().match(/^-{3,}$/)) i++

      // Collect raw data rows
      const dataLines: string[] = []
      while (i < lines.length) {
        const rowTrimmed = lines[i].trim()
        if (!rowTrimmed) break
        if (rowTrimmed.match(/^===.*===\s*$/)) break
        if (rowTrimmed.match(/^-{3,}$/)) {
          i++
          continue
        }
        if (isSectionHeaderLine(lines[i], rowTrimmed)) break
        if (isTableHeaderLine(rowTrimmed)) break
        dataLines.push(lines[i])
        i++
      }

      // Use gap detection across header + all data rows to find true columns
      const allLines = [headerLine, ...dataLines]
      const colStarts = detectColumnStarts(allLines)
      const headers = sliceCells(headerLine, colStarts)
      const rows = dataLines.map(dl => sliceCells(dl, colStarts))

      ensureCurrent()
      current!.tables.push({ headers, rows })
      continue
    }

    // Section header: all-caps, not indented
    const headerMatch = trimmed.match(
      /^([A-Z][A-Z\s]+(?:\([^)]*\))?)\s*(?::\s*(.+))?$/
    )
    if (headerMatch && !line.startsWith(" ")) {
      current = {
        title: headerMatch[1].trim(),
        kvEntries: [],
        tables: [],
        textLines: []
      }
      if (headerMatch[2]) {
        current.textLines.push(headerMatch[2].trim())
      }
      sections.push(current)
      i++
      continue
    }

    ensureCurrent()

    // Skip separator lines
    if (trimmed.match(/^-{3,}$/)) {
      i++
      continue
    }

    // Key-value pair: "Key: Value" (1+ spaces after colon)
    const kvMatch = trimmed.match(/^([^:]+?):\s+(.+)$/)
    if (kvMatch && !trimmed.match(/^https?:/)) {
      current!.kvEntries.push({
        key: kvMatch[1].trim(),
        value: kvMatch[2].trim()
      })
      i++
      continue
    }

    // Plain text
    current!.textLines.push(trimmed)
    i++
  }

  return sections
}

// --- Cell renderer helpers ---

const CacheBadge: FC<{ value: string }> = ({ value }) => {
  const upper = value.toUpperCase()
  if (upper === "HIT") {
    return (
      <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700">
        HIT
      </span>
    )
  }
  if (upper === "MISS") {
    return (
      <span className="inline-flex px-1.5 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-600">
        MISS
      </span>
    )
  }
  return <span>{value}</span>
}

const isCacheValue = (v: string) =>
  v.toUpperCase() === "HIT" || v.toUpperCase() === "MISS"

const renderCellValue = (value: string) => {
  if (isCacheValue(value)) return <CacheBadge value={value} />
  // Bracket type like [ThirdParty]
  const bracketMatch = value.match(/^\[(\w+)\]$/)
  if (bracketMatch) {
    return <span className="text-xs text-gray-500">{bracketMatch[1]}</span>
  }
  // Dollar amount
  if (value.match(/^\$[\d,.]+$/)) {
    return <span className="font-mono">{value}</span>
  }
  return <>{value}</>
}

// --- Section renderer ---

const TableBlock: FC<{ table: GenericTable }> = ({ table }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-neutral-200 bg-gray-50">
          {table.headers.map((h, i) => (
            <th
              key={i}
              className={`py-2 px-3 text-xs font-medium text-gray-500 whitespace-nowrap ${
                i === 0 ? "text-left" : "text-right"
              }`}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y divide-neutral-100">
        {table.rows.map((row, i) => (
          <tr key={i} className="hover:bg-gray-50 transition-colors">
            {row.map((cell, j) => (
              <td
                key={j}
                className={`py-2 px-3 whitespace-nowrap ${
                  j === 0
                    ? "text-left font-medium text-gray-900"
                    : "text-right text-gray-600"
                }`}
              >
                {renderCellValue(cell)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

const SectionBlock: FC<{ section: ParsedSection }> = ({ section }) => (
  <div>
    {section.title && (
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
        {section.title}
      </h4>
    )}

    {section.kvEntries.length > 0 && (
      <div className="space-y-1.5">
        {section.kvEntries.map((kv, i) => (
          <div key={i} className="flex items-baseline justify-between gap-4">
            <span className="text-sm text-gray-500 whitespace-nowrap">
              {kv.key}
            </span>
            <span className="text-sm font-medium text-gray-900 text-right font-mono">
              {isCacheValue(kv.value) ? (
                <CacheBadge value={kv.value} />
              ) : (
                kv.value
              )}
            </span>
          </div>
        ))}
      </div>
    )}

    {section.tables.map((table, i) => (
      <div
        key={i}
        className="border border-neutral-200 rounded-lg overflow-hidden mt-2"
      >
        <TableBlock table={table} />
      </div>
    ))}

    {section.textLines.length > 0 && (
      <div className="space-y-1">
        {section.textLines.map((line, i) => (
          <p key={i} className="text-sm text-gray-700">
            {line}
          </p>
        ))}
      </div>
    )}
  </div>
)

// --- Main component ---

const LodestarDiagnostic: FC = () => {
  const dispatch = useAppDispatch()
  const [mode, setMode] = useState<"inventory" | "lookup">("inventory")
  const [state, setState] = useState("")
  const [zipcode, setZipcode] = useState("")
  const [loanAmount, setLoanAmount] = useState("")
  const [loanPurpose, setLoanPurpose] = useState("")
  const [listPrice, setListPrice] = useState("")
  const [refresh, setRefresh] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<string | null>(null)

  const parsed = useMemo(() => (result ? parseOutput(result) : []), [result])

  const handleSubmit = async () => {
    setIsLoading(true)
    setResult(null)

    try {
      const params: any = { mode, refresh: refresh || undefined }
      if (state) params.state = state
      if (mode === "lookup") {
        if (zipcode) params.zipcode = zipcode
        if (loanAmount) params.loan_amount = Number(loanAmount)
        if (loanPurpose) params.loan_purpose = loanPurpose
        if (listPrice) params.list_price = Number(listPrice)
      }
      const data = await diagnosticsApi.lodestar(params)
      setResult(data)
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to run Lodestar diagnostic"
      dispatch(pushToast({ msg, theme: "danger", timeout: 5000 }))
    } finally {
      setIsLoading(false)
    }
  }

  const copyResult = () => {
    if (!result) return
    navigator.clipboard.writeText(result)
    dispatch(
      pushToast({ msg: "Copied to clipboard", theme: "success", timeout: 2000 })
    )
  }

  return (
    <div className="bg-white border border-neutral-200 rounded-lg">
      <div className="px-6 py-5 border-b border-neutral-100 bg-neutral-50 rounded-t-lg">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <Compass className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              Lodestar Diagnostic
            </h3>
            <p className="text-sm text-gray-500">
              Run Lodestar cache diagnostics and lookups
            </p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Mode:
            </label>
            <select
              value={mode}
              onChange={e => setMode(e.target.value as "inventory" | "lookup")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="inventory">Inventory</option>
              <option value="lookup">Lookup</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              State{mode === "inventory" ? " (optional)" : ""}:
            </label>
            <select
              value={state}
              onChange={e => setState(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select state...</option>
              {US_STATES.map(s => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>

          {mode === "lookup" && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Zipcode:
                </label>
                <input
                  type="text"
                  value={zipcode}
                  onChange={e => setZipcode(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g. 90210"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Loan Amount:
                </label>
                <input
                  type="number"
                  value={loanAmount}
                  onChange={e => setLoanAmount(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g. 350000"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Loan Purpose:
                </label>
                <input
                  type="text"
                  value={loanPurpose}
                  onChange={e => setLoanPurpose(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g. purchase"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  List Price:
                </label>
                <input
                  type="number"
                  value={listPrice}
                  onChange={e => setListPrice(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="e.g. 400000"
                />
              </div>
            </>
          )}

          <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={refresh}
              onChange={e => setRefresh(e.target.checked)}
              className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            Refresh cache
          </label>
        </div>

        <div className="pt-2">
          <PrimaryBtn
            onClick={handleSubmit}
            disabled={isLoading}
            className="w-full justify-center"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Running...
              </>
            ) : (
              "Run Diagnostic"
            )}
          </PrimaryBtn>
        </div>

        {result && (
          <div className="border border-neutral-200 rounded-lg overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-neutral-200 flex items-center justify-between">
              <p className="text-sm font-medium text-gray-700">Results</p>
              <button
                onClick={copyResult}
                className="inline-flex items-center gap-1.5 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded transition-colors"
                title="Copy raw output"
              >
                <Copy className="w-3.5 h-3.5" />
                Copy
              </button>
            </div>

            {parsed.length > 0 ? (
              <div className="p-4 divide-y divide-neutral-100 space-y-4">
                {parsed.map((section, i) => (
                  <div key={i} className={i > 0 ? "pt-4" : ""}>
                    <SectionBlock section={section} />
                  </div>
                ))}
              </div>
            ) : (
              <pre className="p-4 text-xs text-gray-800 overflow-auto max-h-96 bg-gray-50 whitespace-pre-wrap">
                {result}
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default LodestarDiagnostic
